<script lang="ts">
  import Layout from "$app/Layouts/Admin.svelte"
  import { formatDate, timeAgo, toast, wait, xhr } from "$app/utils"
  import { SquarePen, Trash2 } from "lucide-svelte"
  import PostForm from "./PostForm.svelte"
  import CommentForm from "./CommentForm.svelte"

  interface Props {
    channel: ForumChannelType
    channels: ForumChannelType[]
  }

  let props = $props()
  let { channel, channels }: Props = props

  let currentPost: ForumPostType | {} = $state({}) as ForumPostType
  let postStatus = $state("")

  function editPost(post: ForumPostType): void {
    currentPost = post
    postStatus = "edit"
  }

  function updatePost(post: ForumPostType): void {
    postStatus = ""
    const idx = channel.forum_posts.findIndex((b) => b.id === post.id)
    channel.forum_posts[idx] = post
    channel.forum_posts = channel.forum_posts
  }

  async function deletePost(post: ForumPostType): Promise<void> {
    post._selected = true
    channel.forum_posts = channel.forum_posts
    await wait(50)
    if (!confirm("Are you sure you want to delete the selected post?")) {
      post._selected = false
      channel.forum_posts = channel.forum_posts
      return
    }

    try {
      await xhr.delete(`/admin/community_posts/${post.id}`)
      channel.forum_posts = channel.forum_posts.filter((b) => b.id !== post.id)
      toast("Post deleted successfully", "success")
    } catch (e) {
      toast("Error deleting post", "error")
    } finally {
      post._selected = false
      channel.forum_posts = channel.forum_posts
    }
  }

  let currentComment: ForumPostCommentType | {} = $state(
    {}
  ) as ForumPostCommentType
  let commentStatus = $state("")

  function editComment(comment: ForumPostCommentType): void {
    currentComment = comment
    commentStatus = "edit"
  }

  function updateComment(comment: ForumPostCommentType): void {
    commentStatus = ""
    const postIdx = channel.forum_posts.findIndex(
      (b) => b.id === comment.forum_post_id
    )

    const idx = channel.forum_posts[postIdx].comments.findIndex(
      (b) => b.id === comment.id
    )
    channel.forum_posts[postIdx].comments[idx] = comment
    channel.forum_posts[postIdx] = channel.forum_posts[postIdx]
  }

  async function deleteComment(comment: ForumPostCommentType): Promise<void> {
    comment._selected = true
    const postIdx = channel.forum_posts.findIndex(
      (b) => b.id === comment.forum_post_id
    )
    channel.forum_posts[postIdx].comments =
      channel.forum_posts[postIdx].comments
    await wait(50)
    if (!confirm("Are you sure you want to delete the selected comment?")) {
      comment._selected = false
      channel.forum_posts[postIdx].comments =
        channel.forum_posts[postIdx].comments
      return
    }

    try {
      await xhr.delete(`/admin/community_comments/${comment.id}`)
      channel.forum_posts[postIdx].comments = channel.forum_posts[
        postIdx
      ].comments.filter((b) => b.id !== comment.id)
      toast("Comment deleted successfully", "success")
    } catch (e) {
      toast("Error deleting comment", "error")
    } finally {
      comment._selected = false
      channel.forum_posts[postIdx].comments =
        channel.forum_posts[postIdx].comments
    }
  }
</script>

<Layout {...props}>
  <span slot="title">Channel {channel.name}</span>
  <div class="flex justify-start">
    <a
      href="/admin/community"
      class="hover:text-blue-500 hover:underline text-gray-700 text-lg"
      >Back to Community Index</a
    >
  </div>

  <div class="mt-4 border border-gray-500 rounded-lg p-6 shadow bg-white">
    <ul class="space-y-4">
      {#each channel.forum_posts as post}
        <li class="flex justify-between gap-4">
          <div
            class="bg-gray-50 border border-gray-100 rounded py-3 px-5 w-full shadow"
          >
            <div class="w-full flex justify-between">
              <div class="flex space-x-3 py-4">
                <div class="flex-shrink-0">
                  <img
                    class="h-10 w-10 rounded-xl shadow-lg"
                    src={post.user.avatar_thumb_url}
                    alt={post.user.name}
                  />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-800">
                    {post.user.name}
                    <span class="ml-2 text-sm text-gray-600 font-medium"
                      >{timeAgo(post.created_at)}</span
                    >
                  </p>
                  <p class="text-sm text-gray-600">
                    {post.user.title}
                    <span class="text-xs ml-1">
                      {#if post.plan.company_name !== "null"}
                        @ {post.plan.company_name}
                      {:else}
                        @ {post.plan.name}
                      {/if}
                    </span>
                  </p>
                </div>
              </div>
              <div class="pt-4">
                <p class="text-sm text-gray-700">
                  <span class="font-bold">Channel: </span>
                  {post.forum_channel.name}
                </p>
              </div>
            </div>
            <div class="p-4 border border-gray-200 rounded bg-white">
              <div class="ProseMirror">
                {@html post.content}
              </div>
            </div>

            {#if post.comments.length > 0}
              <div class="mt-4 bg-gray-200 border border-blue-200 p-4">
                <h2 class="text-lg font-bold text-gray-700">Comments</h2>
                <ul class="space-y-3 max-h-128 overflow-y-auto mt-3">
                  {#each post.comments as comment}
                    <li class="flex justify-between">
                      <div class="bg-gray-50 rounded py-2 px-4 w-full">
                        <div
                          class="flex space-x-3 py-4 text-gray-700 items-center"
                        >
                          {comment.user_name}
                          <span class="text-gray-500 text-sm ml-2">
                            on {formatDate(comment.created_at)}</span
                          >
                        </div>
                        <div class="border p-4 rounded ProseMirror">
                          {@html comment.content}
                        </div>
                      </div>

                      <div
                        class="flex justify-center gap-6 px-4 py-2 bg-blue-50"
                      >
                        <button onclick={() => editComment(comment)}>
                          <SquarePen
                            class="size-6 text-gray-500 hover:text-blue-500"
                          />
                        </button>
                        <button onclick={() => deleteComment(comment)}>
                          <Trash2
                            class="size-6 text-gray-500 hover:text-red-500"
                          />
                        </button>
                      </div>
                    </li>
                  {/each}
                </ul>
              </div>
            {/if}
          </div>
          <div class="pt-8">
            <p class="text-lg text-center text-white">Actions</p>
            <div class="flex justify-center gap-6 px-4 py-2">
              <button onclick={() => editPost(post)}>
                <SquarePen class="size-6 text-gray-500 hover:text-blue-500" />
              </button>
              <button onclick={() => deletePost(post)}>
                <Trash2 class="size-6 text-gray-500 hover:text-red-500" />
              </button>
            </div>
          </div>
        </li>
      {/each}
    </ul>
  </div>

  <PostForm
    post_id={currentPost.id}
    {channels}
    open={postStatus !== ""}
    status={postStatus}
    on:modalClose={() => (postStatus = "")}
    on:updated={({ detail }) => updatePost(detail)}
  />

  <CommentForm
    comment_id={currentComment.id}
    open={commentStatus !== ""}
    status={commentStatus}
    on:modalClose={() => (commentStatus = "")}
    onupdate={(detail) => updateComment(detail)}
  />
</Layout>
